import { useState, useEffect, useRef, useCallback } from "react";
import BackgroundLayer from "@/components/BackgroundLayer";
import { useAudio } from "@/components/AudioContext";
import { ListenButton } from "@/components/AudioControls";
import { openCookiePrefs } from "@/components/CookieBanner";

// 5 images: P, o, r, d., Pord. full + video
const PORD_IMAGES = [
  "/images/pord_P.jpg",
  "/images/pord_o.jpg",
  "/images/pord_r.jpg",
  "/images/pord_d.jpg",
  "/images/pord_full.jpg",
];

// Structured intro content
const INTRO_SECTIONS = [
  { type: "header", text: "Pord. : In development mode (Sign-up at the end for early access)" },
  { type: "header", text: 'Preview : "Time Capsule" experience' },
  { type: "label", text: "Time Capsule:" },
  { type: "line", text: "2008 : Uncertainty looming" },
  { type: "line", text: "60% in investment portfolio" },
  { type: "line", text: "You're watching it moving. You're not panicking. You're paying attention." },
  { type: "line", text: "3 Decisions to make" },
];

export default function WelcomePage({ onNext, stageIndex }) {
  // Check if user is returning (e.g., from privacy page)
  const isReturning = sessionStorage.getItem("pord_visited") === "true";

  const [currentSlide, setCurrentSlide] = useState(isReturning ? PORD_IMAGES.length - 1 : 0);
  const [showVideo, setShowVideo] = useState(false);
  const [videoEnded, setVideoEnded] = useState(isReturning);
  const [showContent, setShowContent] = useState(isReturning);
  const [visibleLines, setVisibleLines] = useState(isReturning ? INTRO_SECTIONS.length : 0);
  const [showCta, setShowCta] = useState(isReturning);
  const [loopDone, setLoopDone] = useState(isReturning);
  const videoRef = useRef(null);
  const touchStartX = useRef(null);
  const totalSlides = PORD_IMAGES.length;
  const { startBgMusic, playStageAudio } = useAudio();
  const [autoStarted, setAutoStarted] = useState(isReturning);
  const autoTimerRef = useRef(null);

  // First click: start auto-slideshow, voice plays after content appears
  const startAutoSlideshow = useCallback(() => {
    if (autoStarted || isReturning) return;
    setAutoStarted(true);
    startBgMusic();
    let idx = 0;
    autoTimerRef.current = setInterval(() => {
      idx++;
      if (idx < totalSlides) {
        setCurrentSlide(idx);
      } else if (idx === totalSlides) {
        setShowVideo(true);
      } else {
        clearInterval(autoTimerRef.current);
      }
    }, 1200);
  }, [autoStarted, isReturning, startBgMusic, totalSlides]);

  // Cleanup auto timer
  useEffect(() => () => { if (autoTimerRef.current) clearInterval(autoTimerRef.current); }, []);

  // Manual advance (after auto or for browsing)
  const advanceSlide = useCallback(() => {
    if (!autoStarted) {
      startAutoSlideshow();
      return;
    }
    if (showVideo) return;
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide(prev => prev + 1);
    } else {
      setShowVideo(true);
    }
  }, [autoStarted, startAutoSlideshow, currentSlide, totalSlides, showVideo]);

  // Go to previous slide
  const prevSlide = useCallback(() => {
    if (showVideo) {
      setShowVideo(false);
      setCurrentSlide(totalSlides - 1);
      return;
    }
    if (currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
    }
  }, [currentSlide, showVideo, totalSlides]);

  // Touch swipe support
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0) advanceSlide(); // swipe left → next
      else prevSlide(); // swipe right → prev
    }
    touchStartX.current = null;
  };

  // Auto-play video
  useEffect(() => {
    if (showVideo && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, [showVideo]);

  const handleVideoEnd = () => {
    setVideoEnded(true);
    setLoopDone(true);
    setShowVideo(false);
    setCurrentSlide(totalSlides - 1);
    setShowContent(true);
    sessionStorage.setItem("pord_visited", "true");
  };

  const handleVideoClick = () => {
    if (videoRef.current) videoRef.current.pause();
    setVideoEnded(true);
    setLoopDone(true);
    setShowVideo(false);
    setCurrentSlide(totalSlides - 1);
    setShowContent(true);
    sessionStorage.setItem("pord_visited", "true");
  };

  // Dot click navigation
  const goToSlide = (i) => {
    if (i < totalSlides) {
      setShowVideo(false);
      setCurrentSlide(i);
    } else {
      setShowVideo(true);
    }
  };

  // Fade-in lines + voice audio after content shows
  useEffect(() => {
    if (!showContent) return;
    playStageAudio(stageIndex);
    const timers = [];
    INTRO_SECTIONS.forEach((_, i) => {
      timers.push(setTimeout(() => setVisibleLines(i + 1), 400 + i * 900));
    });
    timers.push(setTimeout(() => setShowCta(true), 400 + INTRO_SECTIONS.length * 900 + 500));
    return () => timers.forEach(clearTimeout);
  }, [showContent, playStageAudio, stageIndex]);

  return (
    <div className="page-wrapper" data-testid="welcome-page">
      <BackgroundLayer image="/images/page1_bg.jpg" />
      <div className="content-layer" style={{ minHeight: "100vh", minHeight: "100dvh", display: "flex", flexDirection: "column" }}>

        {/* Image Carousel — always visible */}
        <div
          style={{
            position: "relative",
            cursor: "pointer",
            userSelect: "none",
            WebkitUserSelect: "none",
          }}
          onClick={showVideo ? handleVideoClick : (loopDone ? () => setCurrentSlide(prev => (prev + 1) % totalSlides) : advanceSlide)}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          data-testid="media-carousel"
        >
          {/* Letter images — edge to edge, ~50vh */}
          <div style={{ position: "relative", width: "100%", height: "50vh", overflow: "hidden" }}>
            {PORD_IMAGES.map((src, i) => (
              <img
                key={i}
                src={src}
                alt={`Pord letter ${i + 1}`}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  opacity: !showVideo && currentSlide === i ? 1 : 0,
                  transition: "opacity 0.3s cubic-bezier(0.16,1,0.3,1)",
                  pointerEvents: "none",
                }}
              />
            ))}

            {/* PC click hint overlay — removed */}

            {/* Video layer */}
            {showVideo && (
              <video
                ref={videoRef}
                src="/images/pord_video.mp4"
                muted
                playsInline
                onEnded={handleVideoEnd}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  opacity: showVideo ? 1 : 0,
                  transition: "opacity 0.3s ease",
                }}
                data-testid="pord-video"
              />
            )}
          </div>

          {/* Dot indicator */}
          <div className="dot-indicator" style={{ marginTop: 16 }}>
            {PORD_IMAGES.map((_, i) => (
              <div
                key={i}
                className={`dot ${!showVideo && currentSlide === i ? "active" : ""}`}
                onClick={(e) => { e.stopPropagation(); goToSlide(i); }}
                style={{ cursor: "pointer" }}
              />
            ))}
            <div
              className={`dot ${showVideo ? "active" : ""}`}
              onClick={(e) => { e.stopPropagation(); goToSlide(totalSlides); }}
              style={{ cursor: "pointer" }}
            />
          </div>

          {/* Tap/Swipe/Click hint — always visible */}
          <p style={{
            color: "rgba(230,234,228,0.85)",
            fontSize: "0.75rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            marginTop: 8,
            marginBottom: 16,
            textAlign: "center",
            animation: "breathe 3s ease-in-out infinite",
          }}>
            {loopDone ? "Click or swipe to browse" : "Click image or swipe to continue"}
          </p>
        </div>

        {/* Content Section (appears after carousel completes) */}
        {showContent && (
          <div className="page-content" style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-start", paddingTop: 40, paddingBottom: 80 }}>

            {/* Brand — DM Sans modern font */}
            <div className="animate-in" style={{ marginBottom: 12 }}>
              <h1 className="headline-focus-in" style={{
                fontFamily: "var(--font-body)",
                fontWeight: 400,
                fontSize: "clamp(2.8rem, 7vw, 4.5rem)",
                color: "var(--accent-teal)",
                textShadow: "0 0 40px rgba(184,212,208,0.15)",
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                marginBottom: 4,
              }}>Pord.</h1>
            </div>

            <div className="animate-in animate-in-d2" style={{ marginBottom: 20 }}>
            </div>

            {/* Audio listen button */}
            <div className="animate-in animate-in-d3" style={{ marginBottom: 40 }}>
              <ListenButton stageIndex={stageIndex} />
            </div>

            {/* Fade-in lines */}
            <div className="text-card" style={{ marginBottom: 36 }}>
              {INTRO_SECTIONS.map((item, i) => (
                <p
                  key={i}
                  style={{
                    opacity: visibleLines > i ? 1 : 0,
                    transform: visibleLines > i ? "translateY(0)" : "translateY(8px)",
                    transition: "opacity 0.8s cubic-bezier(0.16,1,0.3,1), transform 0.8s cubic-bezier(0.16,1,0.3,1)",
                    color: item.type === "header" ? "var(--accent-teal)" : item.type === "label" ? "rgba(184,212,208,0.5)" : "rgba(163,163,163,0.7)",
                    fontSize: item.type === "header" ? "0.95rem" : item.type === "label" ? "0.78rem" : "0.95rem",
                    fontWeight: item.type === "header" ? 400 : 300,
                    letterSpacing: item.type === "label" ? "0.12em" : "0.01em",
                    textTransform: item.type === "label" ? "uppercase" : "none",
                    lineHeight: 1.75,
                    marginBottom: item.type === "label" ? 4 : i < INTRO_SECTIONS.length - 1 ? 10 : 0,
                    marginTop: item.type === "label" ? 12 : 0,
                  }}
                >
                  {item.type === "line" ? `- ${item.text}` : item.text}
                </p>
              ))}
            </div>

            {/* Disclaimer */}
            <div className="animate-in animate-in-d4" style={{ marginBottom: 28 }}>
              <p style={{ color: "rgba(230,234,228,0.8)", fontSize: "0.95rem", lineHeight: 1.75, marginBottom: 8 }}>
                Reading the situation correctly is not required.
              </p>
              <p style={{ color: "rgba(230,234,228,0.8)", fontSize: "0.95rem", lineHeight: 1.75, marginBottom: 10 }}>
                Only honest answers are.
              </p>
              <p style={{ color: "rgba(220,224,218,0.7)", fontSize: "0.92rem", marginBottom: 8, lineHeight: 1.7 }}>
                Your responses stayed in your browser. Nothing was recorded.{" "}
                <a href="/privacy" className="ghost-link" style={{ textDecoration: "underline", color: "rgba(184,212,208,0.7)" }} data-testid="privacy-link">
                  See how this works
                </a>
              </p>
            </div>

            {/* CTA */}
            <div style={{
              opacity: showCta ? 1 : 0,
              transform: showCta ? "translateY(0)" : "translateY(12px)",
              transition: "opacity 0.6s cubic-bezier(0.16,1,0.3,1), transform 0.6s cubic-bezier(0.16,1,0.3,1)",
            }}>
              <button className="cta-pill" onClick={onNext} data-testid="enter-capsule-btn">
                Enter the capsule
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer style={{ padding: "20px 24px 60px", textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
            <a href="/privacy" className="footnote-text ghost-link">Privacy</a>
            <a href="/do-not-sell" className="footnote-text ghost-link">Do Not Sell</a>
            <button className="footnote-text ghost-link" onClick={openCookiePrefs} style={{ border: "none", padding: 0, cursor: "pointer" }}>Cookie</button>
          </div>
        </footer>
      </div>
    </div>
  );
}
